import type { MouseEventHandler } from 'react';
import type { ElementType } from '@react-spring/web';
import { noop } from '@tanstack/react-table';

import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import type {
  ButtonOrLinkComponentProps,
  LinkOrButton,
} from '../../styles/clickable-element/clickable-element';
import {
  getClickableElementStyles,
  isValidIconButton,
  isValidLoadingState,
} from '../../styles/clickable-element/clickable-element';
import type { BoxProps } from '../box/box';
import { Box } from '../box/box';
import { LoadingSpinner } from '../loading-spinner/loading-spinner';
import { ScreenReaderText } from '../screen-reader-text/screen-reader-text';
import { Text } from '../text/text';
import {
  type TooltipAriaDisabledProps,
  TooltipAriaDisabled,
} from '../tooltip/tooltip-aria-disabled';

export type ButtonProps = Pick<BoxProps, 'id' | 'value' | 'className' | 'ref'> &
  ButtonOrLinkComponentProps & {
    type?: HTMLButtonElement['type'];
    form?: string;
    formNoValidate?: boolean;
    onClick?: MouseEventHandler;
    'data-testid'?: string;
  };

const TEST_IDS = {
  LEFT_ICON: 'left-icon',
  RIGHT_ICON: 'right-icon',
};

export const Button = ({
  size = 'medium',
  'data-testid': testId,
  onClick,
  children,
  variant = 'solid',
  type = 'button',
  'aria-disabled': ariaDisabled,
  disabled,
  loading = false,
  LeftIcon,
  RightIcon,
  Icon,
  iconColor,
  className,
  ref,
  ...buttonProps
}: ButtonProps) => {
  const { styles, padding, textVariant, iconSize, spacing, resetStyles } =
    getClickableElementStyles({
      ...({ size, variant } as LinkOrButton),
      disabled: disabled || ariaDisabled,
      hasLeftIcon: !!LeftIcon,
      hasRightIcon: !!RightIcon || loading,
    });

  return (
    <Box
      disabled={loading || disabled}
      aria-disabled={ariaDisabled}
      Component="button"
      variant="initial" // css display property set by getClickableElementStyles
      className={classFilter([resetStyles, styles, className])}
      onClick={onClick}
      data-testid={testId}
      type={type}
      ref={ref}
      padding={{ ...padding, y: 'none' }}
      spacing={spacing}
      rounded
      {...buttonProps}
    >
      {LeftIcon ? (
        <LeftIcon data-testid={TEST_IDS.LEFT_ICON} size={iconSize} />
      ) : null}
      {isValidIconButton(variant, Icon) ? (
        <Box>
          <ScreenReaderText>{children}</ScreenReaderText>
          <Icon size={iconSize} color={iconColor} />
        </Box>
      ) : (
        <Text color="currentColor" variant={textVariant}>
          {children}
        </Text>
      )}

      {RightIcon ? (
        <RightIcon data-testid={TEST_IDS.RIGHT_ICON} size={iconSize} />
      ) : null}

      {isValidLoadingState(loading, size) ? (
        <LoadingSpinner
          size={size}
          color={variant === 'solid' ? 'white' : 'primary'}
        />
      ) : null}
    </Box>
  );
};

export const ButtonDisabled = ({
  children,
  variant = 'solid',
  LeftIcon,
  RightIcon,
  Icon,
  ref,
  size,
  ...props
}: Omit<TooltipAriaDisabledProps<ButtonProps>, 'children'> &
  Pick<
    ButtonProps,
    'LeftIcon' | 'RightIcon' | 'Icon' | 'ref' | 'variant' | 'children' | 'size'
  >) => (
  <TooltipAriaDisabled {...props}>
    <Button
      onClick={noop}
      {...(variant === 'icon'
        ? { variant, Icon: Icon as ElementType }
        : { variant, size, LeftIcon, RightIcon })}
      ref={ref}
    >
      {children}
    </Button>
  </TooltipAriaDisabled>
);
